import { useCallback, useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Brand } from "./components/Brand";
import { useAdminSession } from "./lib/session";
import { ConfirmDialog } from "./components/ConfirmDialog";
import {
  LayoutDashboard,
  Building2,
  ShieldCheck,
  ShoppingBag,
  Wallet,
  Banknote,
  Landmark,
  CheckSquare,
  AlertCircle,
  Scale,
  BarChart3,
  FileText,
  LifeBuoy,
  Settings,
  ChevronLeft,
  Bell,
  Menu,
} from "lucide-react";

const mainModules = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/agents", label: "Agents", icon: ShieldCheck },
  { to: "/superdealers", label: "Super Dealers", icon: Building2 },
  { to: "/kyc", label: "KYC Queue", icon: ShieldCheck },
  { to: "/orders", label: "Orders", icon: ShoppingBag },
  { to: "/wallets", label: "Wallets", icon: Wallet },
  { to: "/funding", label: "Funding", icon: Banknote },
  { to: "/dealer-topups", label: "Dealer Top-ups", icon: Landmark },
  { to: "/approvals", label: "Approvals", icon: CheckSquare },
  { to: "/refunds", label: "Refunds", icon: AlertCircle },
  { to: "/recon", label: "Reconciliation", icon: Scale },
  { to: "/finance", label: "Finance", icon: BarChart3 },
  { to: "/reports", label: "Reports", icon: FileText },
  { to: "/settings", label: "Settings", icon: Settings },
];

export function Admin({ children }: { children?: ReactNode }) {
  const { admin, loading, signOut } = useAdminSession();
  const location = useLocation();
  const navigate = useNavigate();
  // The sidebar is an off-canvas drawer below 768px, so it starts closed there.
  const [sidebarOpen, setSidebarOpen] = useState(
    () => !window.matchMedia("(max-width: 768px)").matches,
  );
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Hooks must run unconditionally, before any early return (Rules of Hooks).
  const toggleSidebar = useCallback(() => setSidebarOpen((p) => !p), []);

  // Close the drawer when the viewport becomes mobile-sized.
  useEffect(() => {
    const query = window.matchMedia("(max-width: 768px)");
    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) setSidebarOpen(false);
    };
    query.addEventListener("change", handleChange);
    return () => query.removeEventListener("change", handleChange);
  }, []);

  // Navigating on a phone should dismiss the drawer.
  useEffect(() => {
    if (window.matchMedia("(max-width: 768px)").matches) setSidebarOpen(false);
  }, [location.pathname]);

  const handleSignOut = useCallback(() => {
    signOut();
    navigate("/login");
  }, [signOut, navigate]);

  // Redirect to login if not authenticated (in useEffect to avoid navigate during render)
  useEffect(() => {
    if (!loading && !admin && location.pathname !== "/login") {
      navigate("/login", { replace: true });
    }
  }, [admin, loading, location.pathname, navigate]);

  // Early return — page will re-render after navigate completes
  if (!admin && location.pathname !== "/login") {
    return null;
  }

  const extraHeadings: Record<string, string> = {
    "/support": "Support",
    "/help": "Help Centre",
  };
  const activeRoute = mainModules.find((m) => m.to === location.pathname);
  const pageHeading = activeRoute?.label ?? extraHeadings[location.pathname] ?? "evocha";

  // ── Login route (no sidebar) ──
  if (location.pathname === "/login") return <Outlet />;

  return (
    <div className="admin-shell">
      {/* ── Sidebar ── */}
      <aside
        className={`admin-sidebar ${sidebarOpen ? "admin-sidebar-open" : "admin-sidebar-collapsed"}`}
        aria-label="Main navigation"
      >
        <div className="admin-sidebar-head">
          <Brand compact />
          <button className="ev-icon-btn" aria-label={sidebarOpen ? "Close menu" : "Open menu"} onClick={toggleSidebar}>
            <ChevronLeft size={18} />
          </button>
        </div>

        <nav className="admin-nav" role="navigation">
          <span className="admin-nav-label">Main</span>
          {mainModules.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.to;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={
                  isActive ? "admin-nav-item admin-nav-item-active" : "admin-nav-item"
                }
              >
                <Icon size={18} strokeWidth={1.6} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="admin-sidebar-foot">
          <div className="admin-sidebar-help">
            <span>Need help?</span>
            <a href="https://wa.me/255700000000" target="_blank" rel="noreferrer">
              Contact support
            </a>
          </div>
          <div className="admin-sidebar-user">
            <span className="ev-avatar ev-avatar-sm">
              {(admin?.name ?? "A").charAt(0).toUpperCase()}
            </span>
            <div className="admin-user-text">
              <strong>{admin?.name ?? "Admin"}</strong>
              <small className="ev-body-micro">{admin?.role ?? "Operations"}</small>
            </div>
          </div>
          <button className="ev-btn ev-btn-sm ev-btn-outline" onClick={() => setShowLogoutConfirm(true)}>
            Sign out
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <button
          className="admin-scrim"
          aria-label="Close navigation"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Main ── */}
      <div className="admin-main">
        <header className={`admin-topbar${location.pathname === "/" ? " admin-topbar-dashboard" : ""}`}>
          <div className="admin-topbar-left">
            <button
              className="ev-icon-btn admin-menu-toggle"
              aria-label={sidebarOpen ? "Close menu" : "Open menu"}
              onClick={toggleSidebar}
            >
              {sidebarOpen ? <ChevronLeft size={18} /> : <Menu size={18} />}
            </button>
            <div>
              <div className="ev-eyebrow">Admin portal</div>
              <h1 className="admin-topbar-title">{pageHeading}</h1>
            </div>
          </div>

          <div className="admin-topbar-right">
            <button className="ev-icon-btn" aria-label="Notifications">
              <Bell size={20} />
            </button>
          </div>
        </header>

        <div className="admin-content">
          <div className="page" key={location.pathname}>
            {children ?? <Outlet />}
          </div>
        </div>
      </div>

      {/* ── Sign-out confirmation ── */}
      <ConfirmDialog
        open={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleSignOut}
        title="Sign out?"
        message="Are you sure you want to sign out of the admin portal?"
        confirmLabel="Sign out"
        cancelLabel="Cancel"
      />
    </div>
  );
}
